export enum Role {
  THIRDEYE_ANALYST = "analyst",
  CUSTOMER_SUCCESS = "cyb-customer-success",
  OPERATIONS = "cyb-operations",
  TEST = "cyb-test",
  TENANT_WORKFORCE = "tnt-workforce",
  TENANT_ASSOCIATE_WORKFORCE = "tnt-associate-workforce",
  CYBRILLA_ADMIN = "cyb-admin",
  TENANT_OPERATIONS = "tnt-operations",
  TENANT_ROOT = "tnt-root",
  THIRDEYE_MANAGER = "manager",
  THIRDEYE_OPERATIONS = "operations",
  THIRDEYE_SQLLAB = "sqllab",
  TENANT_INVESTOR = "tnt-investor",

  TENANT_MEMBERSHIP_OPERATION = "tnt-membership-operation",
  TENANT_MEMBERSHIP_CHECKER = "tnt-membership-checker",
  TENANT_MEMBERSHIP_REVIEWER = "tnt-membership-reviewer",
  TENANT_MEMBERSHIP_APPROVER = "tnt-membership-approver",
  TENANT_MEMBERSHIP_MD = "tnt-membership-md",
  TENANT_SETTLEMENT_ASSOCIATE = "tnt-settlement-associate",
  TENANT_INVESTOR_ANALYST = "tnt-investor-analyst",
  TENANT_ORDERS_ANALYST = "tnt-orders-analyst",
  TENANT_ORDER_OPERATIONS = "tnt-order-operations",
  TENANT_SETTLEMENT_REMEDIATION_MAKER = "tnt-settlement-remediation-maker",
  TENANT_SETTLEMENT_REMEDIATION_CHECKER = "tnt-settlement-remediation-checker",

  // amc
  OPERATIONS_ANALYST = "cyb-operations-analyst",
  TENANT_OPERATIONS_ANALYST = "tnt-operations-analyst",
  TENANT_BUSINESS_ANALYST = "tnt-business-analyst",
  TENANT_MARKETING_ASSOCIATE = "tnt-marketing-associate",

  ONDC_ANALYST = "ondc-analyst",
}

export type Roles = Role[];

export type UserInfoResponse = {
  sub: string;
  tenants: string[];
  email_verified: boolean;
  roles: Roles;
  given_name: string;
  family_name: string;
  email: string;
  type: "internal" | "workforce";
  fp_identifier?: string;
};

export const RoleLabelMap: Partial<Record<Role, string>> = {
  analyst: "thirdeye analyst",
  operations: "thirdeye operations",
  manager: "thirdeye manager",
  "cyb-customer-success": "customer success",
  "cyb-admin": "cybrilla admin",
  "cyb-operations": "operations",
  "tnt-workforce": "workforce",
  "tnt-associate-workforce": "associate workforce",
  "tnt-operations": "tenant operations",
  "tnt-root": "tenant root",
  "tnt-investor": "tenant investor",

  "tnt-membership-operation": "tenant membership operation",
  "tnt-membership-checker": "tenant membership checker",
  "tnt-membership-reviewer": "tenant membership reviewer",
  "tnt-membership-approver": "tenant membership approver",
  "tnt-membership-md": "tenant membership md",
  "tnt-settlement-associate": "tenant settlement associate",
  "tnt-investor-analyst": "tenant investor analyst",
  "tnt-orders-analyst": "tenant orders analyst",
  "tnt-order-operations": "tenant order operations",
  "tnt-settlement-remediation-maker": "tenant settlement remediation maker",
  "tnt-settlement-remediation-checker": "tenant settlement remediation checker",

  // amc
  "tnt-operations-analyst": "tenant operations analyst",
  "cyb-operations-analyst": "operations analyst",
  "tnt-business-analyst": "tenant business analyst",
  "tnt-marketing-associate": "tenant marketing associate",

  "ondc-analyst": "ondc analyst",
};

export const RoleDescriptionMap: Partial<Record<Role, string>> = {
  analyst: "Provides access to the BI tool, including SQL Lab, for querying and analyzing data",
  operations: "Provides access to the BI tool but without SQL Lab access for querying data",
  "cyb-customer-success": "",
  "cyb-admin": "",
  "cyb-operations": "",
  "tnt-workforce":
    "Grants access to view investors, orders and payments data where investor's personal data(PII) is not masked",
  "tnt-associate-workforce":
    "Grants access to view investors, orders, and payments data where investor's personal data(PII) is masked",
  "tnt-operations": "Allows uploading of folio feed files",
  "tnt-root": "",
  "tnt-investor": "",

  "tnt-membership-operation":
    "Reviews application documents and performs the initial verification of membership applications.",
  "tnt-membership-checker":
    "Reviews applications verified by the Maker and performs the second-level verification.",
  "tnt-membership-reviewer":
    "Reviews membership applications as part of the HOD-level verification process.",
  "tnt-membership-approver":
    "Reviews and approves membership applications from a regulatory perspective.",
  "tnt-membership-md": "Performs the final review and approval of membership applications.",
  "tnt-settlement-associate":
    "Grants access to view payment and payout-related data, including settlement MIS upload and download, where investor's personal data(PII) is masked",
  "tnt-investor-analyst":
    "Grants access to view investor-related data, including investor verification and validation checks, where investor's personal data(PII) is masked",
  "tnt-orders-analyst":
    "Grants access to view order-related data where investor's personal data(PII) is masked",
  "tnt-order-operations":
    "Grants access to perform order operations, including order feed generation, reverse feed uploads, and other operational activities",
  "tnt-settlement-remediation-maker":
    "Grants access to create deposit receipt adjustments, recon refunds, bank account statements, Manual MIS uploads, and AMC refund Credit MIS where investor's personal data (PII) is masked",
  "tnt-settlement-remediation-checker":
    "Grants access to approve or reject deposit receipt and recon adjustments, reset recon readiness, and AMC refund Credit MIS uploads where investor's personal data (PII) is masked",

  // amc
  "tnt-operations-analyst":
    "Provides access to order processing metrics to streamline and accelerate operations",
  "cyb-operations-analyst": "",
  "tnt-business-analyst":
    "Provides access to metrics information to analyze performance and usage analytics",
  "tnt-marketing-associate": "Provides access to marketing resources",

  "ondc-analyst": "",
};

export const TENANT_ROLES: readonly Role[] = [
  Role.TENANT_ASSOCIATE_WORKFORCE,
  Role.THIRDEYE_ANALYST,
  Role.TENANT_ROOT,
  Role.TENANT_BUSINESS_ANALYST,
  Role.TENANT_INVESTOR,
  Role.THIRDEYE_OPERATIONS,
  Role.TENANT_WORKFORCE,
  Role.TENANT_OPERATIONS,
  Role.TENANT_OPERATIONS_ANALYST,
  Role.THIRDEYE_MANAGER,
  Role.THIRDEYE_SQLLAB,
  Role.TENANT_MARKETING_ASSOCIATE,
  Role.TENANT_MEMBERSHIP_OPERATION,
  Role.TENANT_MEMBERSHIP_CHECKER,
  Role.TENANT_MEMBERSHIP_REVIEWER,
  Role.TENANT_MEMBERSHIP_APPROVER,
  Role.TENANT_MEMBERSHIP_MD,
  Role.TENANT_SETTLEMENT_ASSOCIATE,
  Role.TENANT_INVESTOR_ANALYST,
  Role.TENANT_ORDERS_ANALYST,
  Role.TENANT_ORDER_OPERATIONS,
  Role.TENANT_SETTLEMENT_REMEDIATION_MAKER,
  Role.TENANT_SETTLEMENT_REMEDIATION_CHECKER,
];

export const TenantRootRoles: Role[] = [Role.TENANT_ROOT];

export const InternalRoles: Role[] = [
  Role.THIRDEYE_ANALYST,
  Role.THIRDEYE_OPERATIONS,
  Role.CUSTOMER_SUCCESS,
  Role.OPERATIONS,
  Role.CYBRILLA_ADMIN,
  Role.OPERATIONS_ANALYST,
  Role.ONDC_ANALYST,
];
